<template>
  <b-modal :active="true" full-screen :destroy-on-hide="true">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>
    <div class="container-file-explorer">
      <div id="file-explorer" :key="key"></div>
    </div>
    <image-gallery
      v-if="isGalleryDisplayed"
      v-model="galleryImages"
      :readOnly="isGalleryReadOnly"
      :disableDelete="true"
      @close="hideGallery"
      @onEditInformation="onOnEditInformation"
      :startIndex="galleryStartIndex"
      :toolType="toolType"
    />

    <Viewer3D
      v-if="showViewer3D"
      @close="close3DViwer"
      :filePath="viewer3DPath"
      :displayFileName="displayFileName"
    />

    <b-modal
      :active="true"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
      @close="() => (showExternalUrlData = false)"
      v-if="showExternalUrlData"
    >
      <form>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Add external URL</p>
          </header>
          <section class="modal-card-body">
            <b-field label="Title">
              <b-input
                type="text"
                v-model="externalURLTitle"
                placeholder="Enter title"
                required
              >
              </b-input>
            </b-field>

            <b-field label="URL">
              <b-input
                type="text"
                v-model="externalURL"
                placeholder="Enter URL"
                required
              >
              </b-input>
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <b-button
              label="Close"
              @click="
                () => {
                  showExternalUrlData = false;
                }
              "
            />
            <b-button label="Add" type="is-primary" @click="onAddUrl" />
          </footer>
        </div>
      </form>
    </b-modal>

    <footer class="modal-card-foot">
      <div
        class="dropdown-wrapper"
        v-if="showImageDropDown && !isGalleryDisplayed"
      >
        <b-dropdown
          aria-role="list"
          :close-on-click="true"
          :scrollable="true"
          :max-height="500"
          class="dropdown-up"
          position="is-top-left"
        >
          <template #trigger="{ active }">
            <div class="container-select">
              <label class="dropdown-label">{{ $t('shared.tasks') }}</label>
              <div class="dropdown-input-container">
                <input
                  class="dropdown-input"
                  type="text"
                  :value="dropdownInputText"
                />
                <b-icon
                  :icon="active ? 'menu-up' : 'menu-down'"
                  size="is-small"
                />
              </div>
            </div>
          </template>
          <b-dropdown-item
            aria-role="listitem"
            :value="null"
            @click="handleTaskSelection(null)"
          >
            {{ $t('shared.all') }}
          </b-dropdown-item>

          <template v-for="task in tasks">
            <b-dropdown-item
              aria-role="listitem"
              :value="task._id"
              @click="handleTaskSelection(task._id)"
            >
              {{ task.taskName }}
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </div>

      <b-button @click="close">
        {{ $t('close') }}
      </b-button>

      <b-button @click="onMultipleSelection" v-if="selectMode">
        {{ $t('save') }}
      </b-button>
    </footer>
  </b-modal>
</template>
<script>
import { v4 } from 'uuid';

import axios from 'axios';

import client from '../../config/api';
import DIJVueConfigurations from '../../helpers/DIJVueConfigurations';
import FileExplorer from '../../../public/file-explorer';
import ImageGallery from '../layout/ImageGallery';
import Viewer3D from './3dViewer/Viewer3D';
import ExcludeItemsMixin from '../../mixins/Report/ExcludeItemsMixin';

import {
  REPORTS_TOGGLE_EXCLUDE_ITEMS,
  REPORTS_ITEM_TO_EXCLUDE,
} from '../../store/reports/actions/actionTypes';

import {
  ADD_DOCUMENT,
  RENAME_DOCUMENT,
  DELETE_DOCUMENT,
  MOVE_DOCUMENTS,
  GET_JOB_DOCUMENTS,
  RESTORE_DOCUMENTS,
  EDIT_DOCUMENT_NAME_DESCRIPTION,
} from '../../store/documents/actions/actionTypes';

import { GET_JOB_ATTACHMENTS_COUNT } from '../../store/jobs/actions/actionTypes';

const uuidv4 = v4;
const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'svg',
  'webp',
  'tiff',
];

export default {
  name: 'DocumentsModal',
  components: {
    FileExplorer,
    ImageGallery,
    Viewer3D,
  },
  computed: {
    documents() {
      return [...this.$store.state.DocumentsModule.documents];
    },
    dropdownInputText() {
      if (this.tasks.length === 0) return '';
      const task = this.tasks.find((t) => t._id === this.selectedTask);
      if (task) return task.taskName;
      if (this.tasks) return 'All';
    },
  },
  props: {
    isDocumentModalVisible: {
      type: Boolean,
      default: false,
    },
    selectMode: {
      type: Boolean,
      default: false,
    },
    singleSelectMode: {
      type: Boolean,
      default: false,
    },
    selectImage: {
      type: Boolean,
      default: false,
    },
    toolType: { type: String },
    preSelectedFiles: {
      type: Array,
      default: () => [],
    },
  },

  mixins: [ExcludeItemsMixin],
  data() {
    return {
      fileExplorer: null,
      isGalleryDisplayed: false,
      isGalleryReadOnly: false,
      galleryImages: [],
      key: uuidv4(),
      galleryStartIndex: 0,
      showViewer3D: false,
      viewer3DPath: null,
      displayFileName: '',
      isLoading: false,
      loadingTimeout: null,
      selectedTask: '',
      showImageDropDown: false,
      tasks: [],
      selectedEntries: [],
      showExternalUrlData: false,
      externalURLTitle: '',
      externalURL: '',
    };
  },

  async mounted() {
    // Initialize js-fileexplorer when the component is mounted
    this.initializeFileExplorer();
    this.selectedEntries = [];
    this.tasks = await this.getTasks();
  },

  beforeDestroy() {
    const elements = window.document.querySelectorAll(
      '.fe_fileexplorer_recycle_bin_folder'
    );
    elements.forEach((element) => element.remove());
    this.fileExplorer.Destroy();
  },

  methods: {
    onMultipleSelection() {
      this.$emit('onSelectMultipleFiles', this.selectedEntries);
    },

    close3DViwer() {
      this.showViewer3D = false;
    },
    close() {
      this.hideImageDropdown();
      this.$emit('close');
    },

    async getTasks() {
      const tasks = await this.$store.dispatch('getJobTasks', {
        jobId: this.$route.params.id,
        allTasks: true,
        return: true,
      });
      return tasks;
    },

    handleTaskSelection(taskId) {
      this.selectedTask = taskId;
      const path = [
        ['', 'Root (/)'],
        ['image-gallery', 'Image Gallery', { canmodify: false }],
      ];
      this.fileExplorer.SetPath(path);
    },

    getImageUrl(url) {
      if (!url) return null;
      if (Array.from(url)[0] !== '/') url = `/${url}`;

      const lastPart = url.split('/').pop();
      const allButLast = url.split('/').slice(0, -1).join('/');
      url = `${allButLast}/${encodeURIComponent(lastPart)}`;

      // For local env we need to point to the local s3
      if (window.location.host.includes('localhost')) {
        return `http://localhost:4566/images${url}`;
      }

      return url;
    },

    async onRefresh(folder, refresh) {
      const pathIDs = folder.GetPathIDs();
      this.isGalleryReadOnly = pathIDs[1] === 'image-gallery';

      this.$store
        .dispatch(GET_JOB_DOCUMENTS, {
          jobId: this.$route.params.id,
          pathIDs,
          taskId: this.selectedTask,
        })
        .then((response) => {
          response.forEach((entry) => {
            if (entry.type === 'file') {
              entry.thumb = this.getImageUrl(entry.filePath);
            }
            if (
              this.$store.state.ReportsModule.excludedItems.includes(
                entry.filePath
              ) &&
              window.location.href.includes('/report')
            ) {
              entry.overlay = 'excluded-image-overlay';
            } else {
              delete entry.overlay;
            }

            if (entry.type === 'url') {
              entry.overlay = 'url-overlay';
            }
          });

          refresh(
            response.sort((a, b) => new Date(a.created) - new Date(b.created))
          );
          if (pathIDs[1] === 'image-gallery') {
            this.showImageDropdown();
          } else {
            this.hideImageDropdown();
          }

          this.isLoading = false;
          if (this.preSelectedFiles.length > 0) {
            this.fileExplorer.SetSelectedItems(this.preSelectedFiles);
          }
        });
    },

    onNewFolder(created, folder) {
      const pathIDs = folder.GetPathIDs();
      const id = uuidv4();
      const entry = {
        name: 'New Folder',
        type: 'folder',
        id,
        hash: id,
      };
      if (pathIDs.length > 1) {
        entry.folderId = pathIDs[pathIDs.length - 1];
      }
      const jobId = this.$route.params.id;
      this.$store.dispatch(ADD_DOCUMENT, { jobId, data: entry }).then(() => {
        created(entry);
      });
    },

    onRename(renamed, folder, entry, newName) {
      const newEntry = { ...entry };
      newEntry.name = newName;
      this.$store
        .dispatch(RENAME_DOCUMENT, { id: entry.id, newName })
        .then(() => {
          renamed(newEntry);
        });
    },
    downloadImage(imageUrl, fileName) {
      axios({
        url: imageUrl,
        method: 'GET',
        responseType: 'blob',
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'],
          });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);

          let filename =
            fileName || imageUrl.substring(imageUrl.lastIndexOf('/') + 1);

          const filePart = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);

          const extension = filePart.includes('.')
            ? filePart.substring(filePart.lastIndexOf('.') + 1).toLowerCase()
            : '';

          if (extension && !filename.includes(`.${extension}`)) {
            filename = `${filename}.${extension}`;
          }
          filename = filename.replace(/%20/g, ' ');
          link.setAttribute('download', filename);
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch((error) => {
          console.error('Error downloading image:', error);
        });
    },

    async onInitDownload(
      downloadcallback,
      currentFolder,
      selectedItemIDs,
      selectedFiles
    ) {
      if (!selectedFiles || selectedFiles.length === 0) {
        console.warn('No images to download.');
        return;
      }

      selectedFiles.forEach((item) => {
        const imageUrl = this.getImageUrl(item.filePath);
        this.downloadImage(imageUrl, item.name);
      });
    },

    async onInitUpload(startupload, fileinfo, refresh) {
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout);
      }
      this.isLoading = true;
      const fileName = fileinfo.file.name;
      const jobId = this.$route.params.id;
      // Check attachments count
      const result = await this.$store.dispatch(
        GET_JOB_ATTACHMENTS_COUNT,
        jobId
      );

      if (result.count + 1 > result.max) {
        this.$buefy.toast.open({
          duration: 5000,
          message: `Too many attachments. Maximum allowed is ${result.max} attachments.`,
          type: 'is-danger',
          position: 'is-top',
        });
        this.isLoading = false;
        return;
      }

      const response = await client.post(`/job/${jobId}/presign-upload-file`, {
        fileName,
      });
      const { presignedPostData, path, uniqueFileName } = response.data;
      const dijConfigurations = new DIJVueConfigurations();
      await dijConfigurations.sendPresignedPostData(
        presignedPostData,
        fileinfo.file,
        fileName
      );

      const pathIDs = fileinfo.folder.GetPathIDs();
      const id = uuidv4();
      const entry = {
        name: fileName,
        type: 'file',
        id,
        hash: id,
        filePath: path,
        uniqueFileName,
      };

      if (pathIDs.length > 1) {
        entry.folderId = pathIDs[pathIDs.length - 1];
      }

      this.$store.dispatch(ADD_DOCUMENT, { jobId, data: entry });
      startupload(fileinfo);
      refresh();

      // Hide after 2 seconds (change as needed)
      this.loadingTimeout = setTimeout(() => {
        this.isLoading = false;
        this.loadingTimeout = null;
      }, 2000);
    },

    async onOpenFile(folder, entry) {
      const jobId = this.$route.params.id;
      let path = entry.filePath;
      const fileExt = path.split('.').pop();
      if (IMAGE_EXTENSIONS.includes(fileExt.toLowerCase())) {
        const folderEntries = folder.GetEntries();
        const images = [];
        let index = 0;
        folderEntries.forEach((e) => {
          if (e.type === 'file') {
            const eFileExt = e.filePath ? e.filePath.split('.').pop() : '';
            if (IMAGE_EXTENSIONS.includes(eFileExt.toLowerCase())) {
              let p = e.filePath;
              const p2 = e.filePath;
              if (Array.from(e.filePath)[0] !== '/') {
                p = `/${p}`;
              }
              if (entry.id === e.id) {
                this.galleryStartIndex = index;
              }
              index++;
              const lastPart = p.split('/').pop();
              const allButLast = p.split('/').slice(0, -1).join('/');
              p = `${allButLast}/${encodeURIComponent(lastPart)}`;
              const galleryImage = {
                _id: e._id,
                id: e.id,
                url: p,
                urlNotEncode: p2,
                name: e.galleryName,
                description: e.galleryDescription,
                feEntry: e,
                folder,
              };
              images.push(galleryImage);
            }
          }
        });

        if (this.selectMode) {
          this.$emit('selectFile', {
            url: this.getImageUrl(path),
            fileName: entry.name,
            fileType: 'image',
          });
          this.close();
          return;
        }

        this.galleryImages = images;
        this.isGalleryDisplayed = true;
      } else if (fileExt === 'stl') {
        this.showViewer3D = true;
        this.viewer3DPath = path;
        this.displayFileName = entry.name;
      } else if (entry.type === 'url') {
        let url = (entry.filePath || '').trim();

        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url}`;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        if (Array.from(path)[0] === '/') {
          path = path.substring(1);
        }
        const dijConfigurations = new DIJVueConfigurations();
        const response = await client.post(
          `/job/${jobId}/reportfile-download-url`,
          { key: path, bucketName: dijConfigurations.bucket }
        );

        if (this.selectMode) {
          if (fileExt !== 'pdf') return;
          this.$emit('selectFile', {
            url: response.data,
            fileName: '1',
            fileType: 'application/pdf',
          });
          this.close();
        } else {
          const anchor = document.createElement('a');
          anchor.style.display = 'none';
          anchor.href = response.data;
          anchor.target = '_blank';
          anchor.setAttribute('download', '');

          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }
      }
    },

    // TODO: Delete also childrens of a folder
    onDelete(deleted, folder, ids) {
      if (ids.includes('recycle-bin')) {
        deleted('Can not delete recycle bin entries');
        return;
      }
      this.$store.dispatch(DELETE_DOCUMENT, ids);
      deleted(true);
    },

    onRestoreItems(items, refresh) {
      this.$store.dispatch(RESTORE_DOCUMENTS, items).then(() => {
        refresh();
      });
    },

    onExcludeItem(value, imageUrl) {
      this.isLoading = true;
      this.$store.dispatch(REPORTS_ITEM_TO_EXCLUDE, {
        value,
        id: imageUrl,
      });
      this.$store.dispatch(REPORTS_TOGGLE_EXCLUDE_ITEMS);
      this.$store.dispatch(REPORTS_TOGGLE_EXCLUDE_ITEMS);
    },

    onProcessAndUploadImage(url) {
      this.isLoading = true;
      if (!this.$parent.expandedItem.images) {
        this.$parent.expandedItem.images = [];
      }
      this.$parent.expandedItem.images.push({
        description: '',
        name: '',
        url,
      });
      this.$parent.handleInputInput(this.$parent.expandedItem);
      this.$parent.isDocumentModalVisible = false;
    },

    onMove(moved, srcpath, srcids, destfolder) {
      const pathIDs = destfolder.GetPathIDs();
      this.$store
        .dispatch(MOVE_DOCUMENTS, {
          srcids,
          destid: pathIDs[pathIDs.length - 1],
        })
        .then(() => {
          moved(true);
        });
    },

    showImageDropdown() {
      this.showImageDropDown = true;
    },

    hideImageDropdown() {
      this.showImageDropDown = false;
    },

    onSelChanged(folder, selecteditemsmap) {
      const ids = Object.keys(selecteditemsmap);

      if (this.singleSelectMode && ids.length > 1) {
        const lastSelectedId = ids[ids.length - 1];
        this.fileExplorer.SetSelectedItems([lastSelectedId]);
        this.selectedEntries = folder
          .GetEntries()
          .filter((entry) => entry.id === lastSelectedId);
      } else {
        this.selectedEntries = folder
          .GetEntries()
          .filter((entry) => ids.includes(entry.id));
      }
    },

    async initializeFileExplorer() {
      const self = this;
      let initPath = [['', this.$t('file_explorer.root'), { canmodify: true }]];
      await this.$store.dispatch(GET_JOB_DOCUMENTS, {
        jobId: this.$route.params.id,
        pathIDs: initPath,
      });

      const {
        onRefresh,
        onNewFolder,
        onRename,
        onInitUpload,
        onInitDownload,
        onDelete,
        onMove,
        onOpenFile,
        onRestoreItems,
        onExcludeItem,
        onProcessAndUploadImage,
        showImageDropdown,
        selectMode,
        selectImage,
        toolType,
        showAddUrl,
        onSelChanged,
      } = this;

      let tools = {
        new_folder: true,
        upload: true,
        download: true,
        delete: true,
        item_checkboxes: true,
      };

      if (selectMode) {
        tools = {
          new_folder: false,
          upload: false,
          download: false,
          delete: false,
          item_checkboxes: false,
        };
      }

      if (selectImage) {
        tools = {
          new_folder: false,
          upload: false,
          download: false,
          delete: false,
          item_checkboxes: false,
        };
        initPath = [
          ['', self.$t('file_explorer.root')],
          [
            'image-gallery',
            self.$t('file_explorer.image_gallery'),
            { canmodify: false },
          ],
        ];
      }

      const options = {
        group: 'file-explorer',
        initpath: initPath,
        tools,
        concurrentuploads: 1,
        selectionmode: 'single',

        onselchanged: onSelChanged,

        onrefresh(folder) {
          const recycleBinFolderButton = window.document.getElementsByClassName(
            'fe_fileexplorer_recycle_bin_folder'
          )[0];
          const newFolderButton = window.document.getElementsByClassName(
            'fe_fileexplorer_folder_tool_new_folder'
          )[0];
          const uplodButton = window.document.getElementsByClassName(
            'fe_fileexplorer_folder_tool_upload'
          )[0];
          const clipBoardButton = window.document.getElementsByClassName(
            'fe_fileexplorer_folder_tool_paste'
          )[0];
          const cutButon = window.document.getElementsByClassName(
            'fe_fileexplorer_folder_tool_cut'
          )[0];
          const deleteButton = window.document.getElementsByClassName(
            'fe_fileexplorer_folder_tool_delete'
          )[0];
          const excludeButton = window.document.getElementsByClassName(
            'fe_fileexplorer_exclude_button'
          )[0];
          const includeButton = window.document.getElementsByClassName(
            'fe_fileexplorer_include_button'
          )[0];
          const imageGalleryButton = window.document.getElementsByClassName(
            'fe_fileexplorer_image_gallery_folder'
          )[0];
          const attachButton = window.document.getElementsByClassName(
            'fe_fileexplorer_attach_button'
          )[0];
          const addURLButton = window.document.getElementsByClassName(
            'fe_fileexplorer_add_url_button'
          )[0];

          if (
            folder.GetPathIDs()[1] === 'recycle-bin' ||
            folder.GetPathIDs()[1] === 'image-gallery' ||
            selectMode ||
            selectImage
          ) {
            newFolderButton.classList.add('display-none');
            uplodButton.classList.add('display-none');
            clipBoardButton.classList.add('display-none');
            cutButon.classList.add('display-none');
            deleteButton.classList.add('display-none');
            attachButton.classList.add('display-none');
            if (selectImage) {
              attachButton.classList.remove('display-none');
            }

            if (folder.GetPathIDs()[1] === 'recycle-bin') {
              excludeButton.classList.add('display-none');
              includeButton.classList.add('display-none');
              imageGalleryButton.classList.remove('display-none');
              addURLButton.classList.add('display-none');
            }

            if (folder.GetPathIDs()[1] === 'image-gallery') {
              excludeButton.classList.remove('display-none');
              includeButton.classList.remove('display-none');
              imageGalleryButton.classList.add('display-none');
              addURLButton.classList.add('display-none');
            }

            if (selectMode || selectImage) {
              recycleBinFolderButton.classList.add('display-none');
            }
          } else {
            newFolderButton.classList.remove('display-none');
            uplodButton.classList.remove('display-none');
            clipBoardButton.classList.remove('display-none');
            cutButon.classList.remove('display-none');
            deleteButton.classList.remove('display-none');
            recycleBinFolderButton.classList.remove('display-none');
            excludeButton.classList.add('display-none');
            attachButton.classList.add('display-none');
            includeButton.classList.add('display-none');
            imageGalleryButton.classList.remove('display-none');
            addURLButton.classList.remove('display-none');
          }

          if (!window.location.href.includes('/report')) {
            excludeButton.classList.add('display-none');
            includeButton.classList.add('display-none');
          }

          if (toolType && toolType.includes('editor')) {
            imageGalleryButton.classList.add('display-none');
          }

          onRefresh(folder, (entries) => {
            // Disable auto-sort
            folder.SetAutoSort(false);
            // To forece update clear and reset the current folder
            folder.SetEntries([]);
            folder.UpdateEntries(entries);
          });
        },

        onrename(renamed, folder, entry, newname) {
          onRename(renamed, folder, entry, newname);
          this.RefreshFolders(true);
        },
        oninitupload(startupload, fileinfo, uploadqueuestarted, uploadMeta) {
          console.log('oninitupload order debug', {
            uploadqueuestarted,
            selectedOrder: fileinfo._fe_selectedorder,
            filePath: fileinfo.fullPath,
            uploadMeta,
          });

          onInitUpload(startupload, fileinfo, () => {
            this.RefreshFolders(true);
          });
        },
        oninitdownload(
          downloadcallback,
          currentFolder,
          selectedItemIDs,
          selectedFiles
        ) {
          onInitDownload(
            downloadcallback,
            currentFolder,
            selectedItemIDs,
            selectedFiles
          );
        },
        onopenfile: onOpenFile,
        onmove(moved, srcpath, srcids, destfolder) {
          onMove(moved, srcpath, srcids, destfolder);
        },
        onnewfolder: onNewFolder,
        ondelete: onDelete,
      };

      const selectAllLabel = this.$t('file_explorer.select_all');
      const FileExplorerTool_Select_All = function (fe) {
        if (!(this instanceof FileExplorerTool_Select_All))
          return new FileExplorerTool_Select_All(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_select_all',
          selectAllLabel
        );

        // Handle clicks.
        const ClickHandler = function () {
          fe.SelectAllItems();
        };
        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');

          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();

        fe.addEventListener('update_tool', UpdateToolHandler);

        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const recycleBinLabel = this.$t('file_explorer.recycle_bin');
      const FileExplorerTool_Recycle_Bin = function (fe) {
        if (!(this instanceof FileExplorerTool_Recycle_Bin))
          return new FileExplorerTool_Recycle_Bin(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_recycle_bin_folder',
          recycleBinLabel
        );

        // Handle clicks.
        const ClickHandler = function () {
          const path = [
            ['', 'Root (/)'],
            [
              'recycle-bin',
              self.$t('file_explorer.recycle_bin'),
              { canmodify: false },
            ],
          ];

          fe.SetPath(path);
        };
        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');

          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();

        fe.addEventListener('update_tool', UpdateToolHandler);

        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const imageGalleryLabel = this.$t('file_explorer.image_gallery');
      const FileExplorerTool_Image_Gallery = function (fe) {
        if (!(this instanceof FileExplorerTool_Image_Gallery))
          return new FileExplorerTool_Image_Gallery(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_image_gallery_folder',
          imageGalleryLabel
        );

        // Handle clicks.
        const ClickHandler = function () {
          const path = [
            ['', 'Root (/)'],
            [
              'image-gallery',
              self.$t('file_explorer.image_gallery'),
              { canmodify: false },
            ],
          ];
          fe.SetPath(path);
          showImageDropdown();
        };
        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');

          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();

        fe.addEventListener('update_tool', UpdateToolHandler);

        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const exclude_imagesLabel = this.$t('file_explorer.exclude_images');
      const FileExplorerTool_Exclude_Button = function (fe) {
        if (!(this instanceof FileExplorerTool_Exclude_Button))
          return new FileExplorerTool_Exclude_Button(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_exclude_button',
          exclude_imagesLabel
        );

        // Handle clicks.
        const ClickHandler = function () {
          const items = fe.GetSelectedFolderEntries();
          if (items.length === 0) {
            return;
          }
          items.forEach((item) => {
            const imageUrl = item.filePath;
            onExcludeItem(true, imageUrl);
          });
          const path = [
            ['', 'Root (/)'],
            [
              'image-gallery',
              self.$t('file_explorer.image_gallery'),
              { canmodify: false },
            ],
          ];
          fe.SetPath(path);
        };
        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');

          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();

        fe.addEventListener('update_tool', UpdateToolHandler);

        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const include_imagesLabel = this.$t('file_explorer.include_images');
      const FileExplorerTool_Include_Button = function (fe) {
        if (!(this instanceof FileExplorerTool_Include_Button))
          return new FileExplorerTool_Include_Button(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_include_button',
          include_imagesLabel
        );

        // Handle clicks.
        const ClickHandler = function () {
          const items = fe.GetSelectedFolderEntries();
          if (items.length === 0) {
            return;
          }
          items.forEach((item) => {
            const imageUrl = item.filePath;
            onExcludeItem(false, imageUrl);
          });
          const path = [
            ['', 'Root (/)'],
            [
              'image-gallery',
              self.$t('file_explorer.image_gallery'),
              { canmodify: false },
            ],
          ];
          fe.SetPath(path);
        };
        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');

          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();

        fe.addEventListener('update_tool', UpdateToolHandler);

        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const attach_imagesLabel = this.$t('file_explorer.attach_images');
      const FileExplorerTool_Attach_Button = function (fe) {
        if (!(this instanceof FileExplorerTool_Attach_Button))
          return new FileExplorerTool_Attach_Button(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_attach_button',
          attach_imagesLabel
        );

        // Handle clicks.
        const ClickHandler = function () {
          const items = fe.GetSelectedFolderEntries();
          if (items.length === 0) {
            return;
          }

          items.forEach((item) => {
            const file = item.filePath;

            onProcessAndUploadImage(file);
          });

          const path = [
            ['', 'Root (/)'],
            [
              'image-gallery',
              self.$t('file_explorer.image_gallery'),
              { canmodify: false },
            ],
          ];
          fe.SetPath(path);
        };
        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');

          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();

        fe.addEventListener('update_tool', UpdateToolHandler);

        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const restoreFileLabel = this.$t('file_explorer.restore_file');
      const FileExplorerTool_Recycle_Restore = function (fe) {
        if (!(this instanceof FileExplorerTool_Recycle_Restore))
          return new FileExplorerTool_Recycle_Restore(fe);
        // Register a toolbar button with File Explorer.
        const node = fe.AddToolbarButton(
          'fe_fileexplorer_recycle_bin_restore',
          restoreFileLabel
        );

        const ClickHandler = function () {
          const items = fe.GetSelectedItemIDs();
          if (items.length === 0) {
            return;
          }
          onRestoreItems(items, () => {
            fe.RefreshFolders(true);
          });
        };

        node.addEventListener('click', ClickHandler);
        const UpdateToolHandler = function () {
          const folder = fe.GetCurrentFolder();

          if (folder.GetPathIDs()[1] === 'recycle-bin') {
            node.classList.remove('display-none');

            if (fe.GetSelectedItemIDs().length > 0) {
              node.classList.remove('display-none');
              node.classList.remove('fe_fileexplorer_disabled');
            } else {
              node.classList.add('fe_fileexplorer_disabled');
            }
          } else {
            node.classList.add('display-none');
            node.classList.add('fe_fileexplorer_disabled');
          }

          fe.ToolStateUpdated();
        };

        fe.addEventListener('update_tool', UpdateToolHandler);
        // Cleanly handle the destroy event.
        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      const FileExplorerTool_Add_URL = function (fe) {
        if (!(this instanceof FileExplorerTool_Add_URL))
          return new FileExplorerTool_Add_URL(fe);

        const node = fe.AddToolbarButton(
          'fe_fileexplorer_add_url_button',
          'Add URL'
        );

        const ClickHandler = function () {
          showAddUrl();
        };

        node.addEventListener('click', ClickHandler);

        const UpdateToolHandler = function () {
          node.classList.remove('fe_fileexplorer_disabled');
          fe.ToolStateUpdated();
        };

        fe.ToolStateUpdated();
        fe.addEventListener('update_tool', UpdateToolHandler);

        const DestroyToolHandler = function () {
          node.removeEventListener('click', ClickHandler);
        };
        fe.addEventListener('destroy', DestroyToolHandler);
      };

      if (!window.toolsAdded) {
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Recycle_Bin);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Recycle_Restore);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Add_URL);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Image_Gallery);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Select_All);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Exclude_Button);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Include_Button);
        window.FileExplorer.RegisterTool(1, FileExplorerTool_Attach_Button);
        window.toolsAdded = true;
      }

      const elem = document.getElementById('file-explorer');
      // eslint-disable-next-line no-new
      this.fileExplorer = new window.FileExplorer(elem, options);
      this.fileExplorer
        .GetElements()
        .innerwrap.classList.toggle('fe_fileexplorer_show_item_checkboxes');
    },

    hideGallery() {
      this.isLoading = true;
      if (this.galleryImages[0].folder.GetPath()[1]) {
        const path = [
          ['', this.$t('file_explorer.root')],
          [
            'image-gallery',
            this.$t('file_explorer.image_gallery'),
            { canmodify: true },
          ],
        ];
        this.fileExplorer.SetPath(path);
      } else {
        const path = [['', this.$t('file_explorer.root'), { canmodify: true }]];
        this.fileExplorer.SetPath(path);
      }
      this.isGalleryDisplayed = false;
    },

    async onOnEditInformation(imageInformation) {
      const image = this.galleryImages[imageInformation.index];
      if (image) {
        image.galleryDescription = imageInformation.description;
        image.galleryName = imageInformation.name;
      }

      const payload = {
        _id: image._id,
        galleryDescription: image.description,
        galleryName: image.name,
      };

      await this.$store.dispatch(EDIT_DOCUMENT_NAME_DESCRIPTION, {
        id: image._id,
        payload,
      });

      image.feEntry.galleryDescription = image.galleryDescription;
      image.feEntry.galleryName = image.galleryName;

      image.folder.SetEntry(image.feEntry);
    },

    showAddUrl() {
      console.log('showAddUrl');
      this.externalURLTitle = '';
      this.externalURL = '';
      this.showExternalUrlData = true;
    },

    async onAddUrl() {
      this.isLoading = true;
      const folder = this.fileExplorer.GetCurrentFolder();
      const pathIDs = folder.GetPathIDs();
      const id = uuidv4();
      const { externalURLTitle, externalURL } = this;

      const entry = {
        name: externalURLTitle,
        type: 'url',
        id,
        hash: id,
        filePath: externalURL,
      };
      if (pathIDs.length > 1) {
        entry.folderId = pathIDs[pathIDs.length - 1];
      }
      this.showExternalUrlData = false;

      const jobId = this.$route.params.id;
      this.$store.dispatch(ADD_DOCUMENT, { jobId, data: entry }).then(() => {
        this.fileExplorer.RefreshFolders(true);
        this.isLoading = false;
      });
    },
  },
};
</script>
<style scoped lang="scss">
.modal-card-foot {
  justify-content: flex-end;
}

.container-select {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 1rem;
  border-radius: 4px;
}

.dropdown-wrapper {
  position: absolute;
  top: 70px;
  left: 100px;
}

.dropdown-input {
  height: 27px;
  border: none;
  font-size: 16px;
  background: transparent;
  cursor: default;
}

.dropdown-label {
  font-weight: 600;
  font-size: 17px;
  color: #0056b3;
  margin-bottom: 6px;
  padding-right: 8px;
}

.dropdown-input-container {
  padding: 0.5rem;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background-color: #fff;
}

.dropdown-up {
  position: absolute;
  left: 0;
  bottom: 10%;
  padding: 5px;
  background-color: #f9f9f9;
  border-radius: 1px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

#task-dropdown-item {
  font-size: 16px;
  vertical-align: auto;
}
.dropdown-item.is-active {
  background-color: rgba(33, 33, 33, 0.12);
}

/* Import the CSS for js-fileexplorer */
@import '../../../public/file-explorer.css';

.container-file-explorer {
  height: calc(100% - 77px);
}
#file-explorer {
  height: 100%;
}
</style>

<style>
[data-feid='recycle-bin'] {
  display: none;
}

.fe_fileexplorer_item_checkbox {
  width: 20px;
  height: 20px;
}

.fe_fileexplorer_folder_tool_download {
  /* display: none; */
}

.fe_fileexplorer_image_gallery_folder {
  background-image: url('../../../public/img/gallery.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_recycle_bin_folder {
  background-image: url('../../../public/img/recycle-bin.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_exclude_button {
  background-image: url('../../../public/img/exclude.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_add_url_button {
  background-image: url('../../../public/img/icons/link-variant.svg') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_attach_button {
  background-image: url('../../../public/img/attachment.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_include_button {
  background-image: url('../../../public/img/include.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_select_all {
  background-image: url('../../../public/img/select-all.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}

.fe_fileexplorer_recycle_bin_restore {
  background-image: url('../../../public/img/restore-bin.png') !important;
  background-position: center;
  background-size: 34px 34px;
  image-rendering: auto;
  background-repeat: no-repeat;
}
/* For all iPads in both portrait and landscape */
@media only screen and (min-width: 768px) and (max-width: 1366px) {
  .fe_fileexplorer_navtool_back {
    height: 40px !important;
    background-position: -0px -0px !important;
    background-size: 190px;
  }

  .fe_fileexplorer_navtool_forward {
    height: 40px !important;
    background-position: -100px -0px !important;
    background-size: 190px;
  }

  .fe_fileexplorer_navtool_up {
    height: 40px !important;
    width: 34px !important;
    background-position: -0px -35px !important;
    background-size: 190px;
  }

  .fe_fileexplorer_navtool_history {
    visibility: hidden;
  }

  .fe_fileexplorer_wrap .fe_fileexplorer_navtools button {
    image-rendering: auto !important;
  }
}

.fe_fileexplorer_wrap .fe_fileexplorer_item_icon {
  height: 100px !important;
}

.fe_fileexplorer_wrap .fe_fileexplorer_item_wrap {
  width: 7em !important;
}

.fe_fileexplorer_wrap .fe_fileexplorer_item_icon_file {
  background-position: 9px -96px !important;
  background-size: 260px !important;
}

.fe_fileexplorer_wrap .fe_fileexplorer_item_icon {
  width: 100px !important;
}

.fe_fileexplorer_wrap .fe_fileexplorer_item_icon_folder {
  background-position: -72px -69px !important;
  background-size: 220px;
  image-rendering: auto;
}

.url-overlay .fe_fileexplorer_item_icon_file {
  background-image: url('../../../public/img/icons/link-variant.svg') !important;
  background-size: 60px 300px !important;
  margin-left: 20px !important;
}
.excluded-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
}

.excluded-image-overlay img {
  opacity: 0.7;
}

.excluded-image-overlay::before {
  content: '\26D4';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 30px;
  color: red;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0.7;
}

.display-none {
  display: none;
}
</style>
